import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';
import {
  HiOutlineDocumentText,
  HiOutlineMapPin as HiOutlineLocationMarker,
  HiOutlineBuildingOffice2 as HiOutlineOfficeBuilding,
  HiOutlinePhone,
  HiOutlineMagnifyingGlass as HiOutlineSearch,
} from 'react-icons/hi2';

interface SearchResult {
  business_name: string;
  document_number: string;
  status: string;
  detail_url: string;
}

interface Lead {
  id: number;
  businessName: string;
  documentNumber: string;
  entityType?: string;
  status?: string;
  filingDate?: string;
  principalAddress?: string;
  mailingAddress?: string;
  registeredAgentName?: string;
  registeredAgentAddress?: string;
  officers?: Array<{ title?: string; name?: string; address?: string }>;
  annualReports?: Array<{ year: string; filed_date: string }>;
  documentLinks?: string[];
  notes?: string;
  contactStatus: string;
  lastContactedAt?: string;
  createdAt: string;
  email?: string;
  phone?: string;
}

export default function AdminLeads() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [savedLeads, setSavedLeads] = useState<Lead[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [activeTab, setActiveTab] = useState<'search' | 'saved'>('search');
  const [fetchingDetail, setFetchingDetail] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setSearching(true);
    try {
      const response = await fetch('/api/leads/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ term: searchTerm, max_results: 20 }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Search failed');
      }

      const data = await response.json();
      // Defensive: ensure results is an array
      setSearchResults(Array.isArray(data?.results) ? data.results : []);
    } catch (error: any) {
      console.error('Search error:', error);
      alert(error.message || 'Failed to search businesses');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleFetchDetail = async (result: SearchResult) => {
    setFetchingDetail(true);
    try {
      const response = await fetch('/api/leads/fetch-detail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          detail_url: result.detail_url,
          document_number: result.document_number,
          business_name: result.business_name,
          status: result.status, // Pass status from search result
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch details');
      }

      const data = await response.json();
      setSelectedLead(data.lead);
      loadSavedLeads(); // Refresh saved leads
      alert(data.message || 'Lead saved successfully!');
    } catch (error: any) {
      console.error('Fetch detail error:', error);
      alert(error.message || 'Failed to fetch business details');
    } finally {
      setFetchingDetail(false);
    }
  };

  const loadSavedLeads = async () => {
    try {
      const response = await fetch('/api/leads');
      if (!response.ok) throw new Error('Failed to load leads');

      const data = await response.json();
      // Defensive: ensure leads is an array
      setSavedLeads(Array.isArray(data?.leads) ? data.leads : []);
    } catch (error) {
      console.error('Load leads error:', error);
      setSavedLeads([]);
    }
  };

  const updateLead = async (id: number, updates: any) => {
    try {
      const response = await fetch(`/api/leads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!response.ok) throw new Error('Failed to update lead');

      const data = await response.json();
      setSelectedLead(data.lead);
      loadSavedLeads();
    } catch (error) {
      console.error('Update lead error:', error);
      alert('Failed to update lead');
    }
  };

  const exportToContacts = async (lead: Lead) => {
    try {
      // Create a contact/message entry from the lead
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leadName: lead.businessName,
          leadEmail: lead.registeredAgentName
            ? `${lead.registeredAgentName.toLowerCase().replace(/\s+/g, '.')}@example.com`
            : 'unknown@example.com',
          leadPhone: '',
          contactMethod: 'phone',
          direction: 'outbound',
          subject: `Cold Call - ${lead.businessName}`,
          body: `Lead from Sunbiz search. Address: ${lead.principalAddress || 'N/A'}. Status: ${lead.status || 'N/A'}`,
          adminNote: `Exported from LeadGen. Document #: ${lead.documentNumber}`,
        }),
      });

      if (!response.ok) throw new Error('Failed to export');

      alert('Lead exported to Contacts page successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export lead to contacts');
    }
  };

  const enrichLeadContact = async (lead: Lead) => {
    if (!lead.registeredAgentName) {
      alert('No registered agent name available to enrich');
      return;
    }

    try {
      const response = await fetch('/api/leads/enrich', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lead.registeredAgentName,
          address: lead.registeredAgentAddress || lead.principalAddress,
          leadId: lead.id,
        }),
      });

      if (!response.ok) throw new Error('Failed to enrich contact');

      const data = await response.json();

      if (data.email || data.phone) {
        alert(
          `Found contact info!\nEmail: ${data.email || 'N/A'}\nPhone: ${data.phone || 'N/A'}`
        );
        // Refresh lead data
        loadSavedLeads();
        if (selectedLead?.id === lead.id) {
          setSelectedLead(data.lead);
        }
      } else {
        alert('No contact information found');
      }
    } catch (error) {
      console.error('Enrich error:', error);
      alert('Failed to enrich contact information');
    }
  };

  const enrichOfficerContact = async (officer: any) => {
    if (!officer?.name) {
      alert('No officer name available');
      return;
    }

    try {
      const response = await fetch('/api/leads/enrich', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: officer.name,
          address: officer.address,
        }),
      });

      if (!response.ok) throw new Error('Failed to enrich contact');

      const data = await response.json();

      if (data.email || data.phone) {
        alert(
          `Found contact info for ${officer.name}!\nEmail: ${data.email || 'N/A'}\nPhone: ${data.phone || 'N/A'}`
        );
      } else {
        alert('No contact information found');
      }
    } catch (error) {
      console.error('Enrich error:', error);
      alert('Failed to enrich contact information');
    }
  };

  const getStatusColor = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('active')) return 'bg-green-100 text-green-800';
    if (s.includes('inactive')) return 'bg-gray-100 text-gray-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getContactStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      qualified: 'bg-green-100 text-green-800',
      not_interested: 'bg-gray-100 text-gray-800',
      converted: 'bg-purple-100 text-purple-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-bebas text-[2.6rem] leading-none tracking-[0.03em] text-[#0a2540]">
          Lead Generation
        </h1>
        <p className="font-sans font-light text-sm text-[#94a3b8] mt-1">
          Search Florida businesses and manage your prospecting pipeline
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('search')}
          className={`pb-3 px-4 font-medium transition-colors ${
            activeTab === 'search'
              ? 'text-[#0ea5e9] border-b-2 border-[#0ea5e9]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Search Businesses
        </button>
        <button
          onClick={() => {
            setActiveTab('saved');
            loadSavedLeads();
          }}
          className={`pb-3 px-4 font-medium transition-colors ${
            activeTab === 'saved'
              ? 'text-[#0ea5e9] border-b-2 border-[#0ea5e9]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Saved Leads ({savedLeads.length})
        </button>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div>
          <Card className="p-6 mb-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by business name (e.g., 'cleaning', 'window', 'maintenance')"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="h-12"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={searching || !searchTerm.trim()}
                className="h-12 px-8"
              >
                <HiOutlineSearch className="w-5 h-5 mr-2" />
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </Card>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-4">
              <h2 className="font-semibold text-lg text-gray-900">
                Found {searchResults.length} results
              </h2>
              {searchResults.map((result, idx) => (
                <Card
                  key={idx}
                  className="p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-400" />
                        <h3 className="font-semibold text-lg text-gray-900">
                          {result.business_name}
                        </h3>
                      </div>
                      <div className="flex gap-4 text-sm text-gray-600">
                        <span>Doc #: {result.document_number}</span>
                        <Badge className={getStatusColor(result.status)}>
                          {result.status}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleFetchDetail(result)}
                      disabled={fetchingDetail}
                      variant="outline"
                    >
                      {fetchingDetail ? 'Loading...' : 'View Details & Save'}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {searchResults.length === 0 && searchTerm && !searching && (
            <div className="text-center py-12 text-gray-500">
              <HiOutlineSearch className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No results found for "{searchTerm}"</p>
            </div>
          )}
        </div>
      )}

      {/* Saved Leads Tab */}
      {activeTab === 'saved' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leads List */}
          <div className="lg:col-span-1 space-y-4">
            {savedLeads.map(lead => (
              <Card
                key={lead.id}
                className={`p-4 cursor-pointer transition-all ${
                  selectedLead?.id === lead.id
                    ? 'ring-2 ring-[#0ea5e9] shadow-md'
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelectedLead(lead)}
              >
                <h3 className="font-semibold text-gray-900 mb-2">
                  {lead.businessName}
                </h3>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>Doc #: {lead.documentNumber}</div>
                  <Badge
                    className={getContactStatusColor(
                      lead.contactStatus || 'new'
                    )}
                  >
                    {(lead.contactStatus || 'new').replace('_', ' ')}
                  </Badge>
                </div>
              </Card>
            ))}

            {savedLeads.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <HiOutlineOfficeBuilding className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No saved leads yet</p>
                <p className="text-sm mt-2">
                  Search and save businesses to get started
                </p>
              </div>
            )}
          </div>

          {/* Lead Details */}
          <div className="lg:col-span-2">
            {selectedLead ? (
              <Card className="p-6">
                <div className="space-y-6">
                  {/* Header with Actions */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="font-bebas text-2xl text-gray-900 mb-2">
                        {selectedLead.businessName}
                      </h2>
                      <div className="flex gap-2 flex-wrap">
                        {selectedLead.entityType && (
                          <Badge variant="outline">
                            {selectedLead.entityType}
                          </Badge>
                        )}
                        {selectedLead.status && (
                          <Badge
                            className={getStatusColor(selectedLead.status)}
                          >
                            {selectedLead.status}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Export to Contacts Button */}
                    <Button
                      onClick={() => exportToContacts(selectedLead)}
                      variant="outline"
                      className="gap-2"
                    >
                      <HiOutlinePhone className="w-4 h-4" />
                      Export to Contacts
                    </Button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Status
                    </label>
                    <Select
                      value={selectedLead.contactStatus || 'new'}
                      onValueChange={value =>
                        updateLead(selectedLead.id, { contactStatus: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="not_interested">
                          Not Interested
                        </SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Addresses */}
                  {selectedLead.principalAddress && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <HiOutlineLocationMarker className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">
                          Principal Address
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-line pl-6">
                        {selectedLead.principalAddress}
                      </p>
                    </div>
                  )}

                  {selectedLead.mailingAddress && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <HiOutlineLocationMarker className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">
                          Mailing Address
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-line pl-6">
                        {selectedLead.mailingAddress}
                      </p>
                    </div>
                  )}

                  {/* Registered Agent */}
                  {selectedLead.registeredAgentName && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <HiOutlinePhone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">
                          Registered Agent
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 pl-6">
                        {selectedLead.registeredAgentName}
                      </p>
                      {selectedLead.registeredAgentAddress && (
                        <p className="text-sm text-gray-600 whitespace-pre-line pl-6 mt-1">
                          {selectedLead.registeredAgentAddress}
                        </p>
                      )}

                      {/* Enrich Button */}
                      <Button
                        onClick={() => enrichLeadContact(selectedLead)}
                        variant="outline"
                        size="sm"
                        className="mt-2 ml-6 gap-2"
                      >
                        <HiOutlineSearch className="w-4 h-4" />
                        Find Email & Phone
                      </Button>
                    </div>
                  )}

                  {/* Officers */}
                  {Array.isArray(selectedLead.officers) &&
                    selectedLead.officers.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <HiOutlineDocumentText className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700">
                            Officers & Directors
                          </span>
                        </div>
                        <div className="space-y-2 pl-6">
                          {selectedLead.officers.map((officer, idx) => (
                            <div
                              key={idx}
                              className="text-sm border-b pb-2 last:border-0"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {officer?.title || 'N/A'}:
                                  </span>{' '}
                                  <span className="text-gray-600">
                                    {officer?.name || 'N/A'}
                                  </span>
                                </div>
                                <Button
                                  onClick={() => enrichOfficerContact(officer)}
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1 text-xs"
                                >
                                  <HiOutlineSearch className="w-3 h-3" />
                                  Find Contact
                                </Button>
                              </div>
                              {officer?.address && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {officer.address}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <Textarea
                      value={selectedLead.notes || ''}
                      onChange={e =>
                        setSelectedLead({
                          ...selectedLead,
                          notes: e.target.value,
                        })
                      }
                      onBlur={() =>
                        updateLead(selectedLead.id, {
                          notes: selectedLead.notes,
                        })
                      }
                      placeholder="Add notes about this lead..."
                      rows={4}
                    />
                  </div>

                  {/* Metadata */}
                  <div className="text-xs text-gray-500 pt-4 border-t">
                    <div>Document #: {selectedLead.documentNumber}</div>
                    {selectedLead.filingDate && (
                      <div>Filing Date: {selectedLead.filingDate}</div>
                    )}
                    <div>
                      Added:{' '}
                      {new Date(selectedLead.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="p-12 text-center text-gray-500">
                <HiOutlineOfficeBuilding className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Select a lead to view details</p>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
